/*
 * Engine wrapper. Two backends, one interface.
 *
 *   1. RemoteEngine  — talks to the Render Node/Stockfish API (preferred).
 *   2. WorkerEngine  — runs Stockfish as a Web Worker in the browser
 *                      (fallback when CONFIG.API_BASE is blank or the API fails).
 *
 * Both expose `start()` and `analyze(fen, { depth, movetime, candidates })`.
 * Remote analysis returns { bestmove, info, candidates }.
 */

import { CONFIG } from "./config.js";

/** How long a stopped search may take to print its bestmove before the queue moves on. */
const STOP_SETTLE_MS = 3000;

export class AnalysisCancelledError extends Error {
  constructor(message = "Analysis cancelled") {
    super(message);
    this.name = "AnalysisCancelledError";
  }
}

export class Engine {
  constructor({ createLocal = () => new WorkerEngine() } = {}) {
    this._createLocal = createLocal;
    this._switching = null;
    this._impl = null;
    this._ready = false;
    this._readyPromise = null;
    this._mode = "pending";
    this._startGen = 0;
  }

  get mode() {
    return this._mode;
  }
  get ready() {
    return this._ready;
  }

  async start() {
    if (this._readyPromise) return this._readyPromise;
    const gen = (this._startGen += 1);
    this._readyPromise = (async () => {
      if (CONFIG.API_BASE) {
        const remote = new RemoteEngine(CONFIG.API_BASE);
        const ok = await remote.healthCheck();
        if (this._startGen !== gen) return;
        if (ok) {
          this._impl = remote;
          this._mode = "remote";
          this._ready = true;
          return;
        }
        console.warn("[engine] Remote API not reachable — falling back to browser worker.");
      }
      const local = this._createLocal();
      await local.start();
      if (this._startGen !== gen) {
        local.dispose();
        return;
      }
      this._impl = local;
      this._mode = "local";
      this._ready = true;
    })();
    return Promise.race([
      this._readyPromise,
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Engine start timeout")), 25_000);
      }),
    ]).catch((err) => {
      this._startGen += 1;
      this._readyPromise = null;
      this._ready = false;
      try {
        this._impl?.dispose?.();
      } catch (_) {
        /* noop */
      }
      this._impl = null;
      this._mode = "pending";
      throw err;
    });
  }

  async analyze(fen, opts = {}) {
    if (!this._ready) await this.start();
    try {
      return await this._impl.analyze(fen, opts);
    } catch (err) {
      // The analysis server stopped answering mid-game (a plane, a tunnel, a
      // dropped connection): go on with the engine in the browser.
      if (this._mode !== "remote" || opts.signal?.aborted || !isNetworkError(err)) throw err;
      if (!(await this._switchToLocal())) throw err;
      return this._impl.analyze(fen, opts);
    }
  }

  /** Replace the remote engine with the browser worker (once); false if it cannot start. */
  _switchToLocal() {
    this._switching ||= (async () => {
      const local = this._createLocal();
      try {
        await local.start();
      } catch (e) {
        local.dispose?.();
        console.warn("[engine] browser worker unavailable:", e?.message);
        this._switching = null;
        return false;
      }
      const remote = this._impl;
      this._impl = local;
      this._mode = "local";
      remote?.dispose?.();
      console.warn("[engine] Remote API stopped answering — using the browser worker.");
      return true;
    })();
    return this._switching;
  }

  cancel(reason = "cancelled") {
    this._impl?.cancel?.(reason);
  }

  dispose() {
    this._impl?.dispose?.();
    this._ready = false;
    this._readyPromise = null;
    this._impl = null;
    this._mode = "pending";
  }
}

/** fetch() rejects with a TypeError when there is no connection at all. */
function isNetworkError(err) {
  return err instanceof TypeError;
}

// ---------- Remote (Render backend) ----------
class RemoteEngine {
  constructor(base) {
    this.base = base.replace(/\/$/, "");
    this._controllers = new Set();
  }

  async healthCheck() {
    try {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), CONFIG.API_TIMEOUT_MS);
      const res = await fetch(this.base + "/readyz", { signal: controller.signal });
      clearTimeout(t);
      return res.ok;
    } catch (e) {
      console.warn("[engine] health check failed:", e.message);
      return false;
    }
  }

  async analyze(fen, { depth, movetime, signal, candidates, multipv } = {}) {
    const controller = new AbortController();
    this._controllers.add(controller);
    const onOuterAbort = () => controller.abort();
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener("abort", onOuterAbort, { once: true });
    }
    const t = setTimeout(() => controller.abort(), CONFIG.API_TIMEOUT_MS);
    try {
      const res = await fetch(this.base + "/v1/analyze-position", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fen,
          depth,
          movetime,
          ...(Array.isArray(candidates) && candidates.length ? { candidates } : {}),
          ...(multipv > 1 ? { multipv } : {}),
        }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`API ${res.status}: ${text.slice(0, 100)}`);
      }
      return await res.json();
    } finally {
      clearTimeout(t);
      this._controllers.delete(controller);
      if (signal) signal.removeEventListener("abort", onOuterAbort);
    }
  }

  cancel(reason = "cancelled") {
    for (const controller of this._controllers) {
      try {
        controller.abort(reason);
      } catch (_) {
        /* noop */
      }
    }
    this._controllers.clear();
  }

  dispose() {
    this.cancel("disposed");
  }
}

// ---------- Local (browser Web Worker fallback) ----------
export class WorkerEngine {
  constructor(workerUrl = "/vendor/stockfish/stockfish.js", options = {}) {
    if (typeof workerUrl === "object" && workerUrl) {
      options = workerUrl;
      workerUrl = options.workerUrl || "/vendor/stockfish/stockfish.js";
    }
    this.workerUrl = workerUrl;
    this.createWorker = options.createWorker || (() => new Worker(this.workerUrl));
    this.analysisTimeoutMs = options.analysisTimeoutMs ?? 20_000;
    this.worker = null;
    this.pending = null;
    this.lastInfo = null;
    this._rows = new Map();
    this._queue = [];
    this.syncing = false;
    this._jobTimer = null;
    // Set after "stop" until the stopped search's bestmove has arrived; its
    // trailing lines are dropped, and only then is readyok requested.
    this._awaitingStop = false;
    this._stopTimer = null;
  }

  start() {
    return new Promise((resolve, reject) => {
      try {
        this.worker = this.createWorker();
      } catch (e) {
        reject(e);
        return;
      }
      const worker = this.worker;
      worker.onmessage = (e) => {
        if (this.worker === worker)
          this._onLine(typeof e.data === "string" ? e.data : String(e.data));
      };
      let initialising = true;
      worker.onerror = (err) => {
        if (this.worker !== worker) return;
        const error = err instanceof Error ? err : new Error(err?.message || "WorkerEngine error");
        // The script never loaded (offline, not yet saved on the device):
        // say so now rather than after the init timeout.
        if (initialising) {
          initialising = false;
          clearTimeout(timeout);
          reject(error);
        }
        const queued = this._queue.splice(0);
        queued.forEach((job) => job.reject(error));
        this.syncing = false;
        if (this.pending) {
          const p = this.pending;
          this.pending = null;
          this._clearJobTimer();
          p.reject(error);
        }
      };

      const timeout = setTimeout(() => reject(new Error("Worker init timeout")), 15000);
      this._onReady = () => {
        initialising = false;
        clearTimeout(timeout);
        resolve();
      };
      this._send("uci");
      this._send("isready");
    });
  }

  _send(cmd) {
    if (this.worker) this.worker.postMessage(cmd);
  }

  _clearJobTimer() {
    if (this._jobTimer) {
      clearTimeout(this._jobTimer);
      this._jobTimer = null;
    }
  }

  _rejectPending(error) {
    if (!this.pending) return;
    const p = this.pending;
    this.pending = null;
    this._clearJobTimer();
    p.reject(error);
  }

  _onLine(chunk) {
    chunk.split("\n").forEach((line) => this._handle(line.trim()));
  }

  _handle(line) {
    if (!line) return;
    if (this._awaitingStop) {
      // Stockfish answers isready before a stopped search prints its last
      // lines and bestmove, so wait for the bestmove before asking.
      if (line.startsWith("bestmove")) this._stopSettled();
      return;
    }
    if (line === "uciok") return;
    if (line === "readyok") {
      this.syncing = false;
      if (this._onReady) {
        const cb = this._onReady;
        this._onReady = null;
        cb();
      }
      this._drainQueue();
      return;
    }
    if (this.syncing) return;
    if (line.startsWith("info ") && this.pending) {
      const info = parseInfo(line);
      if (!info) return;
      this.lastInfo = { ...(this.lastInfo || {}), ...info };
      if (info.multipv != null && info.pv?.[0] && (info.scoreCp != null || info.mate != null)) {
        this._rows.set(info.multipv, info);
      }
      return;
    }
    if (line.startsWith("bestmove") && this.pending) {
      const parts = line.split(/\s+/);
      const best = parts[1] && parts[1] !== "(none)" ? parts[1] : null;
      const p = this.pending;
      const info = this.lastInfo;
      const candidates = [...this._rows.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([, row]) => ({
          uci: row.pv[0],
          scoreCp: row.scoreCp ?? null,
          mate: row.mate ?? null,
          pv: row.pv,
        }));
      this.pending = null;
      this.lastInfo = null;
      this._rows = new Map();
      this._clearJobTimer();
      p.resolve({ bestmove: best, info: info || null, candidates });
      this._drainQueue();
    }
  }

  analyze(fen, { depth = 12, movetime, signal, multipv = 1 } = {}) {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error("WorkerEngine is not started"));
        return;
      }
      const job = {
        fen,
        depth,
        movetime,
        signal,
        multipv: Math.max(1, Math.min(64, multipv)),
        resolve,
        reject,
      };
      if (signal?.aborted) {
        reject(new AnalysisCancelledError("aborted"));
        return;
      }
      this._queue.push(job);
      this._drainQueue();
    });
  }

  _drainQueue() {
    if (this.pending || this.syncing || this._queue.length === 0) return;
    const job = this._queue.shift();
    if (job.signal?.aborted) {
      job.reject(new AnalysisCancelledError("aborted"));
      this._drainQueue();
      return;
    }
    this.pending = job;
    this.lastInfo = null;
    this._rows = new Map();
    this._jobTimer = setTimeout(() => {
      if (this.pending !== job) return;
      this.pending = null;
      this._clearJobTimer();
      this._stopSearch();
      job.reject(new Error("WorkerEngine analysis timeout"));
    }, this.analysisTimeoutMs);
    if (job.signal) {
      job.signal.addEventListener(
        "abort",
        () => {
          if (this.pending !== job) return;
          this.pending = null;
          this._clearJobTimer();
          this._stopSearch();
          job.reject(new AnalysisCancelledError("aborted"));
        },
        { once: true }
      );
    }
    const n = job.multipv;
    this._send(`setoption name MultiPV value ${n}`);
    this._send("ucinewgame");
    this._send(`position fen ${job.fen}`);
    if (job.movetime) this._send(`go movetime ${job.movetime}`);
    else this._send(`go depth ${job.depth}`);
  }

  cancel(reason = "cancelled") {
    const queued = this._queue.splice(0);
    const error = new AnalysisCancelledError(reason);
    queued.forEach((job) => job.reject(error));
    if (this.pending) this._stopSearch();
    this._rejectPending(error);
  }

  _stopSearch() {
    this.syncing = true;
    this._awaitingStop = true;
    this._send("stop");
    this._stopTimer = setTimeout(() => {
      if (this._awaitingStop) this._replaceStoppedWorker();
    }, STOP_SETTLE_MS);
  }

  _stopSettled() {
    if (this._stopTimer) {
      clearTimeout(this._stopTimer);
      this._stopTimer = null;
    }
    this._awaitingStop = false;
    this._send("isready");
  }

  _replaceStoppedWorker() {
    this._stopTimer = null;
    this._awaitingStop = false;
    // UCI output has no request IDs. A worker that has not printed its old
    // bestmove cannot safely be reused, even if it answers isready.
    const retired = this.worker;
    this.worker = null;
    retired?.terminate();
    this.lastInfo = null;
    this._rows = new Map();
    this.syncing = true;
    this.start().catch((error) => {
      this.worker?.terminate();
      this.worker = null;
      this.syncing = false;
      this._rejectPending(error);
      this._queue.splice(0).forEach((job) => job.reject(error));
    });
  }

  dispose() {
    this.cancel("disposed");
    if (this._stopTimer) {
      clearTimeout(this._stopTimer);
      this._stopTimer = null;
    }
    this._awaitingStop = false;
    if (this.worker) {
      try {
        this.worker.terminate();
      } catch (_) {
        /* noop */
      }
      this.worker = null;
    }
  }
}

function parseInfo(line) {
  const out = {};
  const d = line.match(/\bdepth (\d+)/);
  if (d) out.depth = parseInt(d[1], 10);
  const mp = line.match(/\bmultipv (\d+)/);
  if (mp) out.multipv = parseInt(mp[1], 10);
  const cp = line.match(/\bscore cp (-?\d+)/);
  if (cp) out.scoreCp = parseInt(cp[1], 10);
  const m = line.match(/\bscore mate (-?\d+)/);
  if (m) out.mate = parseInt(m[1], 10);
  const pv = line.match(/\bpv (.+)$/);
  if (pv) out.pv = pv[1].trim().split(/\s+/);
  return Object.keys(out).length ? out : null;
}
